import type { BulkFallo, BulkResponse, BulkResponseConsolidada } from '../types';

interface OrquestarBatchesParams {
  uids: string[];
  batchSize: number;
  maxConcurrent: number;
  procesarBatch: (batchUids: string[]) => Promise<BulkResponse>;
  /** Se llama cada vez que un batch termina (éxito o fallo), con el total procesado hasta el momento. */
  onProgress?: (procesados: number, total: number) => void;
}

/**
 * Divide `uids` en batches de `batchSize` y los procesa con un pool de workers
 * que mantiene como máximo `maxConcurrent` batches en vuelo a la vez.
 *
 * Un batch que falla por completo (error de red o 500) cuenta como fallido
 * para todos sus UIDs, sin abortar los demás batches.
 */
export async function orquestarBatches({
  uids,
  batchSize,
  maxConcurrent,
  procesarBatch,
  onProgress,
}: OrquestarBatchesParams): Promise<BulkResponseConsolidada> {
  const batches: string[][] = [];
  for (let i = 0; i < uids.length; i += batchSize) {
    batches.push(uids.slice(i, i + batchSize));
  }

  const total = uids.length;
  let procesados = 0;
  const exitosos: string[] = [];
  const fallidos: BulkFallo[] = [];

  let nextIndex = 0;

  async function worker(): Promise<void> {
    for (;;) {
      const batchIndex = nextIndex;
      nextIndex += 1;
      const batch = batches[batchIndex];
      if (!batch) return;

      try {
        const response = await procesarBatch(batch);
        exitosos.push(...response.exitosos);
        fallidos.push(...response.fallidos);
      } catch {
        for (const uid of batch) {
          fallidos.push({
            uid_protocolo: uid,
            error_code: 'internal_error',
            motivo: 'Error inesperado al procesar el protocolo.',
          });
        }
      }

      procesados += batch.length;
      onProgress?.(procesados, total);
    }
  }

  const workerCount = Math.min(maxConcurrent, batches.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return { exitosos, fallidos };
}
